import React, { Fragment, useCallback, useMemo, useState } from "react";

import UiLabelChainScanLink from "@/components/Ui/Label/ChainScanLink";
import Web3ClaimRewardsTx from "@/components/Web3/ClaimRewards/Tx";
import useClaimProtocolRewards from "@/hooks/rewards/useClaimProtocolRewards";
import useProtocolRewardsTokens from "@/hooks/rewards/useProtocolRewardsTokens";
import Web3CommonSwitchNetwork from "@/components/Web3/Common/SwitchNetwork";

import { SupAssetLabel, SupButton, SupText } from "@repo/core-ui";
import { SupCheckCircle, SupSuperformMorph } from "@repo/core-ui";
import { useDynamicContext } from "@dynamic-labs/sdk-react-core";

import Image from "next/image";
import { twMerge } from "tailwind-merge";
import { formatCurrencyCompact } from "@/utils";
import { formatAmount } from "@/utils/tx";
import { useStoredDataDispatch } from "@/store";
import { useClaimRewardsStore } from "@/zstnd/providers/claim-rewards-store-provider";
import useChainInfo from "@/hooks/useChainInfo";

interface Web3ClaimRewardsWrapperProps {
  headerClassName?: string;
}

export default function Web3ClaimRewardsWrapper({ headerClassName }: Web3ClaimRewardsWrapperProps) {
  const { network } = useDynamicContext();
  const storedDispatch = useStoredDataDispatch();

  const { chainId, reset } = useClaimRewardsStore(store => store);
  const { data: chainInfo } = useChainInfo(chainId);

  const [txHash, setTxHash] = useState<string | undefined>();

  const requireSwitchChain = useMemo(() => {
    return network ? parseInt(`${network}`, 10) !== chainId : false;
  }, [chainId, network]);

  const { data: apiData, error: apiError, isLoading: apiIsLoading, mutate } = useClaimProtocolRewards(chainId);

  const handleTxHash = useCallback(
    (hash: string) => {
      setTxHash(hash);
      setTimeout(() => mutate(), 5000);
    },
    [mutate]
  );

  const handleCancel = useCallback(() => {
    reset();
    storedDispatch({ type: "closeOverlay" });
  }, [reset, storedDispatch]);

  const { data } = useProtocolRewardsTokens({ filterByChain: chainId, strategy: "fully-cached", defaultGroupBy: "claimable" });

  return (
    <Fragment>
      <header className={twMerge("border-bottom-[.5px] fixed top-0 z-50 flex w-[370px] items-center justify-between space-x-2 border-neutral-400 py-2.5", headerClassName)}>
        <h2 className="txt-header-dgt my-0 text-[16px] leading-10">CLAIMING REWARDS</h2>
        <SupSuperformMorph className="h-6 w-6 fill-neutral-900" />
      </header>
      <div className="space-y-2">
        <div className="flex h-10 w-full items-center justify-between border-b-[.5px] border-neutral-200">
          {!!chainInfo && (
            <SupAssetLabel
              className="ml-[1px] flex items-center gap-2 text-neutral-900"
              label={
                <SupText variant="caption" weight="bold" className="text-[14px] leading-4">
                  {chainInfo?.name}
                </SupText>
              }
            >
              <Image unoptimized={true} width={14} height={14} src={chainInfo?.icon} alt={chainInfo?.name} />
            </SupAssetLabel>
          )}
          {txHash && (
            <SupText variant="caption" as="div" className="inline-flex items-center gap-2 text-[12px] font-normal leading-4 tracking-[1px] text-success-800">
              <SupCheckCircle />
              <span>Claimed</span>
            </SupText>
          )}
        </div>
        {Object.entries(data).map((token, index) => (
          <div className="inline-flex h-[34px] w-full items-center justify-between gap-4 text-neutral-900" key={`rewards-claim-token-list-${index}`}>
            <SupText variant="subtitle1" className="text-[14px] leading-3 tracking-[1px]">
              {token[1].token.symbol}
            </SupText>
            <div className="flex flex-col items-end justify-center">
              <SupAssetLabel
                className="flex w-fit items-center gap-2"
                label={
                  <SupText variant="subtitle1" className="text-[14px] leading-4">
                    {formatAmount(`${token[1].amount}`, token[1].token.decimals)}
                  </SupText>
                }
              >
                <Image unoptimized={true} width={14} height={14} src={token[1].token.logo} alt={token[1].token.symbol} />
              </SupAssetLabel>
              <SupText variant="caption" className="text-[11px] font-normal leading-4 tracking-[1px] text-neutral-800">
                {formatCurrencyCompact(token[1].amount_usd, 6)}
              </SupText>
            </div>
          </div>
        ))}
      </div>
      {txHash && chainId && (
        <div className="-ml-2 mt-6">
          <UiLabelChainScanLink urlLabel="Explorer" prefixLabel="View on" address={txHash} chain={chainId} />
        </div>
      )}

      <div className="mt-auto flex w-full flex-col items-stretch justify-between space-y-4">
        {txHash ? (
          <SupButton fluid onClick={handleCancel} className="bg-primary-800 hover:opacity-80" color="primary">
            Done
          </SupButton>
        ) : requireSwitchChain ? (
          <Web3CommonSwitchNetwork targetChainId={parseInt(`${chainId}`)} />
        ) : (
          chainId && <Web3ClaimRewardsTx apiData={apiData} apiError={apiError} apiIsLoading={apiIsLoading} handleTxHash={handleTxHash} chainId={chainId} />
        )}
      </div>
    </Fragment>
  );
}
